import { Injectable } from "@angular/core";

export interface IFemaDefinition {
  label: string;
  color: string;
}

const FEMA_LABELS: Record<number, string> = {
  0: "No Damage",
  1: "Affected (Minor Damage)",
  2: "Minor Damage",
  3: "Moderate Damage",
  4: "Significant Damage",
  5: "Major Damage",
  6: "Destroyed",
};

@Injectable({ providedIn: "root" })
export class DamageStyleService {
  private femaMap: Record<number, IFemaDefinition> = {};

  /**
   * Populates the FEMA level → { label, color } map from the raw Mapbox GL style response.
   * Called by MapService after the damage style API request completes.
   * Labels are fixed per level; colors are sourced from the API.
   */
  setStyleData(styleData: any): void {
    const map: Record<number, IFemaDefinition> = {};
    if (!styleData?.layers) {
      this.femaMap = map;
      return;
    }
    for (const layer of styleData.layers) {
      if (layer.type !== "fill") continue;
      const color: string | undefined = layer.paint?.["fill-color"];
      if (!color) continue;

      // Level 0: unaffected layers use id-based naming (filter is a series of != conditions)
      if (String(layer.id).includes("unaffected")) {
        if (!map[0]) {
          map[0] = { label: FEMA_LABELS[0], color };
        }
        continue;
      }

      // Levels 1–6: positive equality filter ["==", "damage_level", "FEMA X / ..."]
      const filter = layer.filter;
      if (!Array.isArray(filter) || filter[0] !== "==" || filter[1] !== "damage_level") continue;
      const levelMatch = String(filter[2]).match(/FEMA (\d+)/);
      if (!levelMatch) continue;
      const level = parseInt(levelMatch[1], 10);
      if (!map[level]) {
        map[level] = { label: FEMA_LABELS[level] ?? filter[2], color };
      }
    }
    this.femaMap = map;
  }

  /** Returns the FEMA level → { label, color } map populated from the API. Empty until {@link setStyleData} is called. */
  getFemaMap(): Record<number, IFemaDefinition> {
    return this.femaMap;
  }
}
